/**
 * useProgramFilters Hook
 * Manages filter state and program filtering logic
 * Extracted from ClubDetails.js
 */

import { useState, useMemo, useCallback } from 'react';
import { filterPrograms } from '../utils/programHelpers';

/**
 * Hook to manage program filtering
 * @param {Array} programs - Array of programs to filter
 * @returns {Object} Filter state and handlers
 */
const useProgramFilters = (programs = []) => {
  // Filter states
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    age: '',
    sport: '',
    gender: '',
    priceRange: null,
    availability: 'all'
  });
  
  // Show/hide filter panel
  const [showFilters, setShowFilters] = useState(false);

  /**
   * Combined filters object
   */
  const combinedFilters = useMemo(() => ({
    query: searchQuery,
    category: selectedCategory,
    ...filters
  }), [searchQuery, selectedCategory, filters]);

  /**
   * Filtered programs based on all criteria
   */
  const filteredPrograms = useMemo(() => {
    return filterPrograms(programs, combinedFilters);
  }, [programs, combinedFilters]);

  /**
   * Update a single filter value
   */
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  /**
   * Reset all filters to default
   */
  const clearFilters = useCallback(() => {
    setSelectedCategory('all');
    setSearchQuery('');
    setFilters({
      age: '',
      sport: '',
      gender: '',
      priceRange: null,
      availability: 'all'
    });
  }, []);

  /**
   * Check if any filters are active
   */
  const hasActiveFilters = useMemo(() => {
    return (
      selectedCategory !== 'all' ||
      searchQuery !== '' ||
      filters.age !== '' ||
      filters.sport !== '' ||
      filters.gender !== '' ||
      filters.priceRange !== null ||
      filters.availability !== 'all'
    );
  }, [selectedCategory, searchQuery, filters]);

  /**
   * Get count of active filters
   */
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedCategory !== 'all') count++;
    if (searchQuery !== '') count++;
    if (filters.age !== '') count++;
    if (filters.sport !== '') count++;
    if (filters.gender !== '') count++;
    if (filters.priceRange !== null) count++;
    if (filters.availability !== 'all') count++;
    return count;
  }, [selectedCategory, searchQuery, filters]);

  /**
   * Handle category change with optional auto-clear of conflicting filters
   */
  const handleCategoryChange = useCallback((categoryId) => {
    setSelectedCategory(categoryId);
  }, []);

  /**
   * Handle search query change with debounce-ready value
   */
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
  }, []);

  /**
   * Toggle filter panel visibility
   */
  const toggleFilters = useCallback(() => {
    setShowFilters(prev => !prev);
  }, []);

  return {
    // Filtered results
    filteredPrograms,
    
    // Category state
    selectedCategory,
    setSelectedCategory: handleCategoryChange,
    
    // Search state
    searchQuery,
    setSearchQuery: handleSearchChange,
    
    // Filter state
    filters,
    setFilters,
    updateFilter,
    
    // Filter panel state
    showFilters,
    setShowFilters,
    toggleFilters,
    
    // Utilities
    clearFilters,
    hasActiveFilters,
    activeFilterCount,
    
    // Combined state for external use
    combinedFilters
  };
};

export default useProgramFilters;










